import type { FormInstance } from "antd/es/form/hooks/useForm"
import type { ComponentType } from "react"

import {
  TEMPLATE_BLOCK_TYPE,
  type TemplateBlockType
} from "../../constants/template-block-type.tsx"
import type {
  DocumentScopeSectionProjectTypes,
  DocumentWarrantyItem
} from "../../types/document.types.ts"
import type {
  TemplateDocumentCostLineItem,
  TemplateDocumentFormData,
  TemplateDocumentScheduleItem,
  TemplateDocumentScopeSectionPhase
} from "../../types/template-document-form-data.tsx"
import { type BlockColumnConfig } from "./TemplateDocumentForm/TemplateDocumentFormLabel/TemplateDocumentFormColumnOptions.tsx"
import { type BlockVariant } from "./TemplateDocumentForm/TemplateDocumentFormLabel/TemplateDocumentFormLabel.tsx"
import { CompanyInfoBlock } from "./common-blocks/CompanyInfoBlock.tsx"
import { CostSummaryBlock } from "./common-blocks/CostSummaryBlock.tsx"
import { CustomerInfoBlock } from "./common-blocks/CustomerInfoBlock.tsx"
import { DocumentListSection } from "./common-blocks/DocumentListSection.tsx"
import { PaymentScheduleBlock } from "./common-blocks/PaymentScheduleBlock.tsx"
import { ProjectOverviewBlock } from "./common-blocks/ProjectOverviewBlock.tsx"
import { SalesPriceBlock } from "./common-blocks/SalesPriceBlock.tsx"
import { ScopeOfWorkBlock } from "./common-blocks/ScopeOfWorkBlock/ScopeOfWorkBlock.tsx"
import { WarrantyBlock } from "./common-blocks/WarrantyBlock.tsx"
import { AppendixDocumentBlock } from "./primitive-block/AppendixDocumentBlock.tsx"
import { BulletListBlock } from "./primitive-block/BulletListBlock.tsx"
import { CustomTableBlock } from "./primitive-block/CustomTableBlock/CustomTableBlock.tsx"
import { DividerBlock } from "./primitive-block/DividerBlock.tsx"
import { FreeTextBlock } from "./primitive-block/FreeTextBlock.tsx"
import { HeaderBlock } from "./primitive-block/HeaderBlock.tsx"
import { ImageBlock } from "./primitive-block/ImageBlock.tsx"
import { InitialSignatureBlock } from "./primitive-block/InitialSignatureBlock.tsx"
import { SectionHeaderBlock } from "./primitive-block/SectionHeaderBlock.tsx"
import {
  type SignatureBlockProps,
  SignatureDateBlock
} from "./primitive-block/SignatureDateBlock.tsx"

interface BuildBlockVariantsOptions {
  form: FormInstance<TemplateDocumentFormData>
  projectTypes?: DocumentScopeSectionProjectTypes
  isTemplate?: boolean
  /** Only used by the Sales Price block's Restore action, to look up the
   * Lead's current `sales_price` at click time. */
  leadId?: string
  /**
   * Default `signer_label` text, keyed by block id: a template can hold
   * several signature blocks of one type.
   */
  initialValues?: Record<string, string>
}

export function buildBlockVariants({
  form,
  isTemplate,
  initialValues,
  leadId,
  projectTypes = []
}: BuildBlockVariantsOptions): Partial<
  Record<TemplateBlockType, BlockVariant>
> {
  // Every signature block type is the same card — a caption, plus the signer
  // slot and its fields in the builder — so a new one is a line, not a copy.
  const signatureBlockVariant = (
    Component: ComponentType<SignatureBlockProps>,
    label: string
  ): BlockVariant => ({
    label,
    titleReadonly: true,
    alignment: true,
    component: ({ block, disabled }, index) => (
      <Component
        fieldKey={block?.id}
        initialValue={initialValues?.[block?.id]}
        form={form}
        index={index}
        disabled={disabled}
        isTemplate={isTemplate}
      />
    )
  })

  return {
    [TEMPLATE_BLOCK_TYPE.HEADER]: {
      label: "Document Header",
      titleReadonly: true,
      required: true,
      component: <HeaderBlock />
    },
    [TEMPLATE_BLOCK_TYPE.COMPANY_INFO]: {
      label: "Company Info",
      component: <CompanyInfoBlock form={form} />
    },
    [TEMPLATE_BLOCK_TYPE.CLIENT_INFO]: {
      label: "Client Information",
      component: ({ disabled }) => (
        <CustomerInfoBlock readonly={isTemplate} disabled={disabled} />
      )
    },
    [TEMPLATE_BLOCK_TYPE.PROJECT_OVERVIEW]: {
      label: "Project Overview",
      component: <ProjectOverviewBlock form={form} />
    },
    [TEMPLATE_BLOCK_TYPE.SALES_PRICE]: {
      label: "Sales Price",
      component: <SalesPriceBlock leadId={leadId} />
    },
    [TEMPLATE_BLOCK_TYPE.SCOPE_OF_WORK]: {
      label: "Scope of Work",
      columns: [
        { key: "name", label: "Scope Of Work", required: false },
        { key: "duration", label: "Duration", required: false },
        { key: "budget", label: "Cost", required: false }
      ] satisfies BlockColumnConfig<
        keyof TemplateDocumentScopeSectionPhase | "duration"
      >[],
      component: ({ disabled }, index) => (
        <ScopeOfWorkBlock
          disabled={disabled || isTemplate}
          form={form}
          index={index}
          projectTypes={projectTypes}
          listRef={null}
        />
      )
    },
    [TEMPLATE_BLOCK_TYPE.INCLUSIONS]: {
      label: "Inclusions",
      component: (
        <DocumentListSection
          form={form}
          name="inclusions"
          itemPlaceholder="Inclusion item"
          addButtonText="Inclusion"
        />
      )
    },
    [TEMPLATE_BLOCK_TYPE.EXCLUSIONS]: {
      label: "Exclusions",
      component: (
        <DocumentListSection
          form={form}
          name="exclusions"
          itemPlaceholder="Exclusion item"
          addButtonText="Exclusion"
        />
      )
    },
    [TEMPLATE_BLOCK_TYPE.CLIENT_RESPONSIBILITIES]: {
      label: "Client Responsibilities",
      component: (
        <DocumentListSection
          form={form}
          name="client_responsibilities"
          itemPlaceholder="Client responsibility"
          addButtonText="Responsibility"
        />
      )
    },
    [TEMPLATE_BLOCK_TYPE.COST_SUMMARY]: {
      label: "Cost Summary",
      columns: [
        { key: "item", label: "Item", required: false },
        { key: "description", label: "Description", required: false },
        { key: "amount", label: "Amount", required: false }
      ] satisfies BlockColumnConfig<keyof TemplateDocumentCostLineItem>[],
      component: (_, index) => <CostSummaryBlock form={form} index={index} />
    },

    [TEMPLATE_BLOCK_TYPE.PAYMENT_SCHEDULE]: {
      label: "Payment Schedule",
      columns: [
        { key: "milestone", label: "Stage", required: false },
        { key: "description", label: "Description", required: false },
        { key: "amount", label: "Amount", required: false },
        { key: "percentage", label: "Percentage", required: false }
      ] satisfies BlockColumnConfig<keyof TemplateDocumentScheduleItem>[],
      component: (_, index) => (
        <PaymentScheduleBlock form={form} index={index} />
      )
    },
    [TEMPLATE_BLOCK_TYPE.WARRANTY]: {
      label: "Warranty",
      columns: [
        { key: "warranty_type", label: "Type", required: false },
        { key: "duration", label: "Duration", required: false },
        { key: "covers", label: "Covers", required: false },
        { key: "excludes", label: "Excludes", required: false }
      ] satisfies BlockColumnConfig<keyof DocumentWarrantyItem>[],
      component: (_, index) => <WarrantyBlock form={form} index={index} />
    },
    [TEMPLATE_BLOCK_TYPE.TERMS_AND_CONDITIONS]: {
      label: "Terms and Conditions",
      component: (
        <DocumentListSection
          form={form}
          name="terms_and_conditions"
          infoPlaceholder="Terms and Conditions"
          itemPlaceholder="Terms and Conditions item"
          addButtonText="Terms and Conditions"
        />
      )
    },
    //
    [TEMPLATE_BLOCK_TYPE.FREE_TEXT]: {
      label: "Free Text",
      titleReadonly: true,
      alignment: true,
      component: ({ block }) => <FreeTextBlock fieldKey={block?.id} />
    },
    [TEMPLATE_BLOCK_TYPE.BULLET_LIST]: {
      label: "Bullet List",
      titleReadonly: true,
      alignment: true,
      component: ({ block }) => (
        <BulletListBlock form={form} fieldKey={block?.id} />
      )
    },
    [TEMPLATE_BLOCK_TYPE.CUSTOM_TABLE]: {
      label: "Custom Table",
      titleReadonly: true,
      alignment: true,
      component: ({ block }) => <CustomTableBlock fieldKey={block?.id} />
    },
    [TEMPLATE_BLOCK_TYPE.IMAGE]: {
      label: "Image",
      titleReadonly: true,
      alignment: true,
      component: ({ block }) => <ImageBlock fieldKey={block?.id} form={form} />
    },
    [TEMPLATE_BLOCK_TYPE.SIGNATURE_DATE]: signatureBlockVariant(
      SignatureDateBlock,
      "Signature & Date"
    ),
    [TEMPLATE_BLOCK_TYPE.INITIAL_SIGNATURE]: signatureBlockVariant(
      InitialSignatureBlock,
      "Initial Signature"
    ),
    [TEMPLATE_BLOCK_TYPE.APPENDIX_DOCUMENT]: {
      label: "Appendix Document",
      titleReadonly: true,
      component: ({ block, disabled }) => (
        <AppendixDocumentBlock
          disabled={disabled}
          fieldKey={block?.id}
          form={form}
        />
      )
    },
    [TEMPLATE_BLOCK_TYPE.SECTION_HEADER]: {
      label: "Section Header",
      titleReadonly: true,
      alignment: true,
      component: ({ block }) => <SectionHeaderBlock fieldKey={block?.id} />
    },
    [TEMPLATE_BLOCK_TYPE.DIVIDER]: {
      label: "Divider",
      titleReadonly: true,
      component: <DividerBlock />
    }
  }
}
